import { useContext } from 'react';
import { financingDisclaimers } from 'axl-config';
import { DisclaimerText, Link } from 'reaxl';
import { formatCurrency } from '@atc/string-fns';
import DisclaimerWithModalContent from '../DisclaimerWithModalContent/DisclaimerWithModalContent.js';
import ConfigContext from '../ConfigContext/ConfigContext.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import buildCashDisclaimer from '../NativeDealCalculator/helpers/buildCashDisclaimer.js';
import buildLeaseDisclaimer from '../NativeDealCalculator/helpers/buildLeaseDisclaimer.js';
import buildFinanceDisclaimer from '../NativeDealCalculator/helpers/buildFinanceDisclaimer.js';
function createContactDisclaimer(type, financePrivateSellerAvailabilityNote) {
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("br", {}), /*#__PURE__*/jsx("br", {}), type === 'finance' && `${financePrivateSellerAvailabilityNote} `, "Please", ' ', /*#__PURE__*/jsx(Link, {
      href: "https://www.autotrader.com/email-us?contactEmail=atc_feedback",
      target: "_blank",
      rel: "noindex nofollow",
      children: "contact customer service"
    }), ' ', "for more information."]
  });
}

/**
 * Determines the appropriate disclaimer text based on context
 * @param {boolean} isOfferBreakdownModal - Whether in offer breakdown modal
 * @param {Object} modalData - Modal data containing modalText
 * @param {string} onPageText - Default on-page text
 * @returns {string} Appropriate disclaimer text
 */
function getDisclaimerText(isOfferBreakdownModal, modalData, onPageText) {
  if (isOfferBreakdownModal) {
    return modalData?.modalText || onPageText;
  }
  return onPageText;
}

/**
 * Creates disclaimer content with conditional modal wrapper
 * @param {boolean} hasModal - Whether disclaimer has modal capability
 * @param {boolean} isOfferBreakdownModal - Whether in offer breakdown modal
 * @param {string} onPageText - On-page disclaimer text
 * @param {string} disclaimerText - Resolved disclaimer text
 * @param {Object} modalData - Modal data for DisclaimerWithModalContent
 * @param {Object} container - Container for modal
 * @returns {JSX.Element|string} Disclaimer content
 */
function createDisclaimerContent(hasModal, isOfferBreakdownModal, onPageText, disclaimerText, modalData, container) {
  const shouldShowModal = hasModal && !isOfferBreakdownModal;
  if (shouldShowModal) {
    return /*#__PURE__*/jsx(DisclaimerWithModalContent, {
      mainText: onPageText,
      ...modalData,
      container: container
    });
  }
  return disclaimerText;
}

/**
 * Builds finance disclaimer content
 * @param {Object} params - Finance disclaimer parameters
 * @returns {JSX.Element|string} Finance disclaimer content
 */
function buildFinanceDisclaimerContent(params) {
  const {
    restPaymentsInfoProps,
    hasTax,
    contactDisclaimer,
    disclaimerKey,
    shortCustomDisclaimer,
    deepLinkHref,
    isOfferBreakdownModal,
    container,
    dealerFeeText
  } = params;
  const disclaimerData = buildFinanceDisclaimer(restPaymentsInfoProps, hasTax, contactDisclaimer, disclaimerKey, shortCustomDisclaimer, deepLinkHref, dealerFeeText);
  const {
    financeDisclaimerHasModal,
    onPageFinanceDisclaimer,
    ...financeDisclaimerModalData
  } = disclaimerData;
  const disclaimerText = getDisclaimerText(isOfferBreakdownModal, financeDisclaimerModalData, onPageFinanceDisclaimer);
  return createDisclaimerContent(financeDisclaimerHasModal, isOfferBreakdownModal, onPageFinanceDisclaimer, disclaimerText, financeDisclaimerModalData, container);
}

/**
 * Builds lease disclaimer content
 * @param {Object} params - Lease disclaimer parameters
 * @returns {JSX.Element|string} Lease disclaimer content
 */
function buildLeaseDisclaimerContent(params) {
  const {
    restPaymentsInfoProps,
    disclaimerKey,
    isOfferBreakdownModal,
    container,
    dealerFeeText
  } = params;
  const disclaimerData = buildLeaseDisclaimer(restPaymentsInfoProps, disclaimerKey, dealerFeeText);
  const {
    leaseDisclaimerHasModal,
    onPageLeaseDisclaimer,
    ...leaseDisclaimerModalData
  } = disclaimerData;
  const disclaimerText = getDisclaimerText(isOfferBreakdownModal, leaseDisclaimerModalData, onPageLeaseDisclaimer);
  return createDisclaimerContent(leaseDisclaimerHasModal, isOfferBreakdownModal, onPageLeaseDisclaimer, disclaimerText, leaseDisclaimerModalData, container);
}

/**
 * Builds cash disclaimer content
 * @param {Object} params - Cash disclaimer parameters
 * @returns {JSX.Element|string} Cash disclaimer content
 */
function buildCashDisclaimerContent(params) {
  const {
    cash,
    tradeEquity,
    hasTax,
    contactDisclaimer,
    disclaimerKey,
    shortCustomDisclaimer,
    isOfferBreakdownModal,
    container,
    dealerFeeText
  } = params;
  const disclaimerData = buildCashDisclaimer(cash, tradeEquity, hasTax, contactDisclaimer, disclaimerKey, shortCustomDisclaimer, dealerFeeText);
  const {
    cashDisclaimerHasModal,
    onPageCashDisclaimer,
    ...cashDisclaimerModalData
  } = disclaimerData;
  const disclaimerText = getDisclaimerText(isOfferBreakdownModal, cashDisclaimerModalData, onPageCashDisclaimer);
  return createDisclaimerContent(cashDisclaimerHasModal, isOfferBreakdownModal, onPageCashDisclaimer, disclaimerText, cashDisclaimerModalData, container);
}

/**
 * Renders appropriate disclaimers for the Native Deal Calculator based on deal type, payment information,
 * and placement context. Supports finance, lease, and cash deal types with conditional modal content.
 *
 * @param {Object} props - Component props
 * @param {Object} [props.container] - Container object for modal rendering
 * @param {Object} [props.disclaimerPaymentProps={}] - Payment information object containing financial details
 * @param {string} [props.disclaimerPaymentProps.cash] - Cash payment amount
 * @param {string} [props.disclaimerPaymentProps.tax='0'] - Tax amount
 * @param {string} [props.disclaimerPaymentProps.tradeEquity] - Trade-in equity value
 * @param {'finance'|'lease'|'cash'} [props.type='finance'] - Type of deal for disclaimer content
 * @param {'default'|'offerBreakdownModal'} [props.placement='default'] - Rendering context/placement
 * @param {Object} [props.rest] - Additional props passed to DisclaimerText component
 * @returns {JSX.Element|null} Rendered disclaimer content or null if no disclaimer applies
 */
function Disclaimer(_ref) {
  let {
    container,
    disclaimerPaymentProps = {},
    type = 'finance',
    placement = 'default',
    ...rest
  } = _ref;
  const isOfferBreakdownModal = placement === 'offerBreakdownModal';
  const {
    disclaimerKey,
    esntialDeepLink = {},
    shortCustomDisclaimer
  } = useContext(ConfigContext) || {};
  const {
    cash,
    tax = '0',
    tradeEquity,
    lenderFees,
    dealerFees,
    ...restPaymentsInfoProps
  } = disclaimerPaymentProps;
  const {
    financePrivateSellerAvailabilityNote
  } = financingDisclaimers;
  const hasTax = tax !== '0';
  const contactDisclaimer = disclaimerKey === 'PSX' ? createContactDisclaimer(type, financePrivateSellerAvailabilityNote) : null;
  const dealerFeeText = (() => {
    const lenderFeesValue = lenderFees ? formatCurrency(Math.floor(lenderFees)) : null;
    const dealerFeesValue = dealerFees ? formatCurrency(Math.floor(dealerFees)) : null;
    if (lenderFeesValue && dealerFeesValue) {
      return ` Includes ${dealerFeesValue} Documentation Fee, ${lenderFeesValue} Prep Fee.`;
    }
    if (dealerFeesValue) {
      return ` Includes ${dealerFeesValue} Documentation Fee.`;
    }
    if (lenderFeesValue) {
      return ` Includes ${lenderFeesValue} Prep Fee.`;
    }
    return '';
  })();
  const disclaimerParams = {
    restPaymentsInfoProps,
    hasTax,
    contactDisclaimer,
    disclaimerKey,
    shortCustomDisclaimer,
    isOfferBreakdownModal,
    container,
    dealerFeeText
  };
  let disclaimer = '';
  switch (type) {
    case 'finance':
      disclaimer = buildFinanceDisclaimerContent({
        ...disclaimerParams,
        deepLinkHref: esntialDeepLink.href
      });
      break;
    case 'lease':
      disclaimer = buildLeaseDisclaimerContent(disclaimerParams);
      break;
    case 'cash':
      disclaimer = buildCashDisclaimerContent({
        ...disclaimerParams,
        cash,
        tradeEquity
      });
      break;
  }
  return disclaimer ? /*#__PURE__*/jsx(DisclaimerText, {
    size: 100,
    ...rest,
    children: disclaimer
  }) : null;
}
export { Disclaimer as default };
